/**
 * Admin Wave AW7 — ローカル写真ファイルと CSV の写真ファイル名列とのマッチング (docs/108 High3)。
 *
 * old2 `apps/admin/src/lib/import/photoMatcher.ts` を踏襲。4 方式:
 *   1. 完全一致
 *   2. MAP_Images/ プレフィックス除去
 *   3. basename (パス部分除去)
 *   4. rowId プレフィックス一致 (AppSheet: `{rowId}.photo.{ts}.jpg`)
 *
 * v2 では MappedRow を受け取り、rowIndex → File の対応を返す。
 */

import type { MappedRow } from './columnMapping';

export interface PhotoMatchResult {
  /** rowIndex (MappedRow.rowIndex) → 対応 File。 */
  matched: Map<number, File>;
  /** マッチしなかった rowIndex 一覧。 */
  unmatched: number[];
  /** 使われなかったファイル名一覧。 */
  unusedFiles: string[];
}

export function matchPhotos(rows: MappedRow[], files: File[]): PhotoMatchResult {
  const nameMap = new Map<string, File>();
  for (const f of files) {
    nameMap.set(f.name, f);
    const rel = (f as File & { webkitRelativePath?: string }).webkitRelativePath;
    if (rel) nameMap.set(rel, f);
  }
  const usedFiles = new Set<string>();
  const matched = new Map<number, File>();
  const unmatched: number[] = [];

  for (const r of rows) {
    if (!r.photoFilename) {
      unmatched.push(r.rowIndex);
      continue;
    }
    const file = findMatch(r.photoFilename, nameMap);
    if (file) {
      matched.set(r.rowIndex, file);
      usedFiles.add(file.name);
    } else {
      unmatched.push(r.rowIndex);
    }
  }

  const unusedFiles = files.filter((f) => !usedFiles.has(f.name)).map((f) => f.name);
  return { matched, unmatched, unusedFiles };
}

function findMatch(filename: string, nameMap: Map<string, File>): File | undefined {
  // 1. 完全一致
  if (nameMap.has(filename)) return nameMap.get(filename);

  // 2. MAP_Images/ プレフィックス除去
  const stripped = filename.replace(/^MAP_Images\//i, '');
  if (stripped !== filename && nameMap.has(stripped)) return nameMap.get(stripped);

  // 3. basename (パス部分除去)
  const basename = filename.includes('/') ? (filename.split('/').pop() ?? filename) : filename;
  if (basename !== filename && nameMap.has(basename)) return nameMap.get(basename);

  // 4. rowId プレフィックス一致 (AppSheet: {rowId}.photo.{ts}.jpg)
  const m = filename.match(/([a-f0-9-]+)\.photo\./i);
  if (m) {
    const prefix = m[1]!;
    for (const [name, file] of nameMap) {
      if (name.includes(prefix)) return file;
    }
  }
  return undefined;
}
